<script setup>
import { useFilterSidePanel } from '@dashhudson/dashing-ui/filter-side-panel';
import {
  PANEL_TYPES,
  SidePanelProvider as DashingSidePanelProvider,
  useCloseOnRouteChange,
  useSidePanel,
} from '@dashhudson/dashing-ui/side-panel';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import without from 'lodash/without';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import {
  FILTER_ACTION_TYPES,
  trackCompetitiveMentionsFilter,
  trackFilterDrawerInteraction,
} from '@/components/dashingUI/constants/mixpanel';

const props = defineProps({
  disableClickOutside: {
    type: Boolean,
    default: false,
  },
});

const route = useRoute();

const prevFilters = ref({});
const prevChanges = ref({});

function onFilterApply({
  scopedFilters: currentFilters,
  changedFilters,
  defaultFilters: initialFilters,
}) {
  if (!isEqual(changedFilters, prevChanges.value)) {
    const updatedFilterKeys = Object.keys(currentFilters).filter(
      (key) =>
        !isEqual(currentFilters[key], prevFilters.value[key]) &&
        (Object.keys(changedFilters).includes(key) || Object.keys(prevChanges.value).includes(key)),
    );

    updatedFilterKeys.forEach((updatedFilterKey) => {
      if (updatedFilterKey) {
        const prevSelected = prevFilters.value[updatedFilterKey];
        const currentSelected = currentFilters[updatedFilterKey];
        const defaultSelected = initialFilters[updatedFilterKey];

        let isRemoved = isEqual(defaultSelected, currentSelected) || !currentSelected;
        if (Array.isArray(defaultSelected)) {
          isRemoved = !currentSelected?.length || prevSelected?.length > currentSelected?.length;
        }

        trackFilterDrawerInteraction({
          action: isRemoved ? FILTER_ACTION_TYPES.REMOVE : FILTER_ACTION_TYPES.APPLY,
          filterName: updatedFilterKey,
          selectedOptions: currentFilters[updatedFilterKey],
        });

        trackCompetitiveMentionsFilter({
          updatedFilterKey,
          currentFilters,
          routeName: route.name,
        });
      }
    });
    prevFilters.value = cloneDeep(currentFilters);
    prevChanges.value = cloneDeep(changedFilters);
  }
}

const { visible, type } = useSidePanel();

const {
  resetFilterNames,
  currentScope,
  filterPanels,
  scopedFilters,
  changedFilters,
  scopedDefaultFilters,
} = useFilterSidePanel();

const collapseData = computed(() =>
  Object.keys(filterPanels.value).filter((k) => filterPanels.value[k].collapsed === true),
);

const isFilterView = computed(() => type.value === PANEL_TYPES.FILTER || type.value === null);

watch(
  () => scopedFilters.value,
  (currentFilters) => {
    onFilterApply({
      scopedFilters: currentFilters,
      changedFilters: changedFilters.value,
      defaultFilters: scopedDefaultFilters.value,
    });
  },
  { deep: true, flush: 'post' },
);

watch(
  () => resetFilterNames.value,
  (to, from) => {
    if (from && to.length) {
      const resetAll = resetFilterNames.value.length === Object.keys(scopedFilters.value).length;
      const action = resetAll ? FILTER_ACTION_TYPES.RESET_ALL : FILTER_ACTION_TYPES.RESET_FILTERSET;
      const filterName = resetAll ? undefined : resetFilterNames.value[0];

      trackFilterDrawerInteraction({
        action,
        filterName,
      });

      if (resetAll) {
        prevFilters.value = { ...scopedFilters.value };
        prevChanges.value = {};
      }
    }
  },
  { deep: true },
);

watch(
  () => visible.value,
  (to, from) => {
    if (!isFilterView.value) return;

    if (to) {
      document.body.dataset.filterSidePanelOpen = '';
      // PAUSED (sc-184888): trackFilterDrawerInteraction({ action: FILTER_ACTION_TYPES.OPENED });
    } else {
      delete document.body.dataset.filterSidePanelOpen;
      if (from) {
        // PAUSED (sc-184888): trackFilterDrawerInteraction({ action: FILTER_ACTION_TYPES.CLOSED });
      }
    }
  },
  { immediate: true },
);

watch(
  () => currentScope.value,
  () => {
    prevFilters.value = { ...scopedFilters.value };
  },
);

watch(
  () => collapseData.value,
  (to, from) => {
    if (!isEqual(to, from)) {
      const lastCollapsed = without(to, ...from)[0];
      const lastExpanded = without(from, ...to)[0];
      if (lastCollapsed) {
        trackFilterDrawerInteraction({
          action: FILTER_ACTION_TYPES.COLLAPSE,
          filterName: filterPanels.value[lastCollapsed].title,
        });
      }
      if (lastExpanded) {
        trackFilterDrawerInteraction({
          action: FILTER_ACTION_TYPES.EXPAND,
          filterName: filterPanels.value[lastExpanded].title,
        });
      }
    }
  },
);

useCloseOnRouteChange(() => route.name);
</script>

<template>
  <DashingSidePanelProvider :disable-click-outside="props.disableClickOutside" />
</template>

<style lang="postcss">
[data-dash-filter-side-panel] {
  top: var(--dash-top-banner-offset, 0) !important;
}
</style>
